import { useNavigate, useParams } from "react-router";
import { AlertCircle, Trash2 } from "lucide-react";
import {
  useCourseCertificateDocument,
  useCourseCertificateDocumentFile,
  useDeleteCourseCertificate,
} from "@/hooks/useCourseCertificates";
import { eftHashPath } from "@cle/constants/routes";
import { createCourseCertificateValidationItems } from "@cle/utils/courseCertificateValidation";
import { shouldShowSupplementaryCard } from "@cle/utils/supplementaryDocumentation";
import { allFieldsApproved } from "@cle/utils/fieldValidationHelpers";
import {
  getEvidenceStatusLabel,
  isAnalyzingStatus,
  isApprovedStatus,
  isAwaitingReviewStatus,
  isRejectedByAiStatus,
  isRejectedStatus,
  type EvidenceStatus,
} from "@cle/models/evidenceItem";
import { parseDateString } from "@/util/date";
import CleButton from "@cle/components/CleButton";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import CourseCertificateDocumentSkeleton from "./components/CourseCertificateDocumentSkeleton";
import CourseCertificateInfoCard from "./components/CourseCertificateInfoCard";
import DocumentValidationCard from "./components/DocumentValidationCard";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import SupplementaryDocumentationCard from "./components/SupplementaryDocumentationCard";
import UploadedDocumentPreviewCard from "./components/UploadedDocumentPreviewCard";

const MISSING_VALUE = "-";

const getStatusVariant = (
  status: EvidenceStatus,
): "danger" | "info" | "success" | "warning" => {
  if (isApprovedStatus(status)) {
    return "success";
  }

  if (isAwaitingReviewStatus(status)) {
    return "warning";
  }

  if (isRejectedStatus(status) || status === "failed") {
    return "danger";
  }

  return "info";
};

const CourseCertificateDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: courseCertificate,
    isError: isCourseCertificateError,
    isLoading: isCourseCertificateLoading,
  } = useCourseCertificateDocument(id);

  const {
    data: documentFile,
    isError: isDocumentFileError,
    isLoading: isDocumentFileLoading,
  } = useCourseCertificateDocumentFile(id);

  const deleteCourseCertificateMutation = useDeleteCourseCertificate();
  const certificateTitle = courseCertificate?.fileName ?? "Kursusbevis.pdf";
  const isReported = courseCertificate?.isReported ?? false;
  const isDeleteDisabled =
    id.length === 0 || deleteCourseCertificateMutation.isPending || isReported;
  const validationItems =
    createCourseCertificateValidationItems(courseCertificate);

  const handleDeleteCourseCertificate = (closeConfirmation: () => void) => {
    deleteCourseCertificateMutation.mutate(id, {
      onSuccess: () => {
        closeConfirmation();
        void navigate(eftHashPath("course-certificates"));
      },
    });
  };

  if (isCourseCertificateLoading) {
    return <CourseCertificateDocumentSkeleton />;
  }

  return (
    <div className="cle-course-certificate-document">
      {isCourseCertificateError ? (
        <div className="alert alert-warning" role="alert">
          Kursusbevisets oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {deleteCourseCertificateMutation.isError ? (
        <div className="alert alert-warning" role="alert">
          Kursusbeviset kunne ikke slettes lige nu. Prøv igen.
        </div>
      ) : null}
      {courseCertificate && isRejectedByAiStatus(courseCertificate.status) ? (
        <div
          style={{ position: "relative" }}
          className="border rounded-3 overflow-hidden fs-6 fw-semibold mb-4 bg-white  p-4 d-flex align-items-center gap-2"
          role="alert"
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "0.25rem",
              height: "100%",
              backgroundColor: "#c43333",
            }}
          />
          <AlertCircle size={26} aria-hidden style={{ color: "#c43333" }} />
          <div className="d-flex flex-column ">
            <p className="mb-0">Kursusbeviset er blevet afvist af systemet.</p>
            <p style={{ fontSize: "0.85rem" }} className="mb-0 opacity-75">
              {allFieldsApproved(courseCertificate.fieldValidation)
                ? "Oplysningerne opfylder nu kravene, men afgørelsen er ikke opdateret. Kontakt sekretariatet længere nede på siden, hvis beviset fortsat står som afvist."
                : "Se i valideringen herunder, hvilke krav der ikke er opfyldt. Mener du, at det er en fejl, kan du vedhæfte supplerende dokumentation og sende en besked til sekretariatet længere nede på siden."}
            </p>
          </div>
        </div>
      ) : null}
      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <UploadedDocumentPreviewCard
              certificateTitle={certificateTitle}
              fileBlob={documentFile}
              isError={isDocumentFileError}
              isLoading={isDocumentFileLoading}
            />
            {courseCertificate &&
            shouldShowSupplementaryCard(courseCertificate) ? (
              <SupplementaryDocumentationCard
                evidence={courseCertificate}
                kind="kursusbevis"
              />
            ) : null}
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <CourseCertificateInfoCard
              action={
                <ConfirmationModal
                  title="Slet kursusbevis"
                  description="Er du sikker på, at du vil slette kursusbeviset? Handlingen kan ikke fortrydes."
                  confirmLabel="Slet"
                  confirmVariant="destructive"
                  confirmButtonId="cle-confirm-delete-course-certificate"
                  cancelButtonId="cle-cancel-delete-course-certificate"
                  isConfirming={deleteCourseCertificateMutation.isPending}
                  isConfirmDisabled={isDeleteDisabled}
                  onConfirm={({ close }) =>
                    handleDeleteCourseCertificate(close)
                  }
                  trigger={
                    <CleButton
                      id="cle-delete-course-certificate"
                      variant="destructive"
                      size="sm"
                      disabled={isDeleteDisabled}
                    >
                      <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                      {deleteCourseCertificateMutation.isPending
                        ? "Sletter..."
                        : "Slet"}
                    </CleButton>
                  }
                >
                  <h3 className="mb-0 text-center">
                    Du er ved at slette et kursusbevis
                  </h3>
                  <p className="mb-0 text-center">
                    Kursusbeviset og det uploadede dokument vil blive slettet
                    permanent.
                  </p>
                </ConfirmationModal>
              }
              isReported={courseCertificate?.isReported ?? false}
              statusLabel={
                courseCertificate
                  ? getEvidenceStatusLabel(courseCertificate.status)
                  : isCourseCertificateLoading
                    ? "Indlæser"
                    : MISSING_VALUE
              }
              statusVariant={
                courseCertificate
                  ? getStatusVariant(courseCertificate.status)
                  : "info"
              }
              title={certificateTitle}
              uploadedAt={
                courseCertificate?.createdAt
                  ? parseDateString(courseCertificate.createdAt)
                  : MISSING_VALUE
              }
            />
            <DocumentValidationCard
              isAnalyzing={
                courseCertificate
                  ? isAnalyzingStatus(courseCertificate.status)
                  : false
              }
              items={validationItems}
            />
            <SecretariatMessageCard subjectId={id} isReported={isReported} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCertificateDocumentView;
